import { useState, useMemo, useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import Layout from "../components/Layout";
import NextButton from "../components/NextButton";
import type { StepComponentType } from "../../hooks/IncorporationWizardFormContext";
import { useIncorporationWizardFormCtx } from "../../hooks/IncorporationWizardFormContext";
import {
  GESTION_DIRECTORES,
  SI_NO_OPTIONS,
  type IncorporationWizardInitialData,
  type GestionDirectoresOption,
  CARGO_DIRECTOR,
} from "@/schemas/incorporation";
import {
  createOrUpdateIncorporationRequest,
  IncorporationRequestError,
} from "@/services/incorporationRequest";
import {
  createOrUpdateIncorporationContact,
  deleteIncorporationContact,
} from "@/services/incorporationContact";
import { CustomTooltip } from "../../CustomTooltip";
import { isDirectorContact, isShareholderContact } from "../../contact-helper";
import type { ContactFormValues } from "../../schema";

const GestionDirectoresStep: StepComponentType = () => {
  const { t } = useTranslation();
  const { data, setData, next } = useIncorporationWizardFormCtx();

  const gestionDirectores = data?.incorporationRequest?.gestion_directores;
  const operaPanama = data?.incorporationRequest?.opera_panama;

  // "abogado" option is disabled when opera_panama is "si"
  const isAbogadoDisabled = operaPanama === SI_NO_OPTIONS[0];

  // Get director contacts for delete logic
  const directorContacts = useMemo<ContactFormValues[]>(
    () =>
      (data?.incorporationContact || [])
        .filter((contact) => isDirectorContact(contact))
        .map((contact) => ({
          ...contact,
          porcentaje_beneficio: contact.porcentaje_beneficio || 0,
        })),
    [data?.incorporationContact]
  );

  const [validationError, setValidationError] = useState<string | null>(null);
  const [gestionDirectoresError, setGestionDirectoresError] = useState<
    string | null
  >(null);
  const [isSavingGestionDirectores, setIsSavingGestionDirectores] =
    useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const gestionDirectoresOptions = [
    {
      value: GESTION_DIRECTORES[0], // "abogado"
      label: t("directors.gestion_directores.lawyer.label"),
      subtitle: t("directors.gestion_directores.lawyer.subtitle"),
      description: (
        <div className="d-flex flex-column gap-3 mt-4">
          <h4 className="fw-semibold size-18">
            {t("directors.gestion_directores.lawyer.description.title")}
          </h4>
          <ul className="ps-0 ms-3">
            <li>
              {t("directors.gestion_directores.lawyer.description.bullet_1")}
            </li>
            <li>
              {t("directors.gestion_directores.lawyer.description.bullet_2")}
            </li>
            <li>
              {t("directors.gestion_directores.lawyer.description.bullet_3")}
            </li>
            <li>
              {t("directors.gestion_directores.lawyer.description.bullet_4")}
            </li>
          </ul>
        </div>
      ),
      disabled: isAbogadoDisabled,
      tooltip: {
        text: t("directors.gestion_directores.lawyer.tooltip_text"),
      },
      badge_text: t("directors.gestion_directores.lawyer.badge_text"),
    },
    {
      value: GESTION_DIRECTORES[1], // "cliente"
      label: t("directors.gestion_directores.client.label"),
      subtitle: t("directors.gestion_directores.client.subtitle"),
      description: (
        <div className="d-flex flex-column gap-3 mt-4">
          <h4 className="fw-semibold size-18">
            {t("directors.gestion_directores.client.description.title")}
          </h4>
          <ul className="ps-0 ms-3">
            <li>
              {t("directors.gestion_directores.client.description.bullet_1")}
            </li>
            <li>
              {t("directors.gestion_directores.client.description.bullet_2")}
            </li>
            <li>
              {t("directors.gestion_directores.client.description.bullet_3")}
            </li>
          </ul>
        </div>
      ),
      disabled: false,
    },
  ];

  // Handle gestion_directores change - save to API immediately
  const handleGestionDirectoresChange = async (
    value: GestionDirectoresOption
  ) => {
    setGestionDirectoresError(null);
    setIsSavingGestionDirectores(true);
    try {
      const incorporationRequestId = data?.incorporationRequest?.id;

      if (!incorporationRequestId) {
        return;
      }

      const requestData = {
        ...data?.incorporationRequest,
        gestion_directores: value,
      };

      const response = await createOrUpdateIncorporationRequest(requestData);

      setData((prevData) => {
        if (!prevData) return prevData;
        return {
          ...prevData,
          incorporationRequest: {
            ...prevData.incorporationRequest,
            ...response.data,
            gestion_directores: value,
            id: response.id,
          },
          incorporationContact: prevData.incorporationContact || [],
          currentUser: prevData.currentUser,
          incorporationStripePrice: prevData.incorporationStripePrice,
          path: prevData.path,
        };
      });
    } catch (error) {
      if (error instanceof IncorporationRequestError) {
        const errorMessage =
          error.errors && error.errors.length > 0
            ? error.errors.join(", ")
            : error.message;
        setGestionDirectoresError(errorMessage);
      } else {
        const errorMessage =
          error instanceof Error ? error.message : "Error al guardar los datos";
        setGestionDirectoresError(errorMessage);
      }
    } finally {
      setIsSavingGestionDirectores(false);
    }
  };

  // If the company operates in Panama (abogado is disabled), force gestion_directores to "cliente"
  useEffect(() => {
    if (!isAbogadoDisabled) return;

    // If already set to "cliente", nothing to do
    if (gestionDirectores === GESTION_DIRECTORES[1]) return;

    // Auto-select "cliente" when "abogado" or undefined
    void handleGestionDirectoresChange(GESTION_DIRECTORES[1]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAbogadoDisabled, gestionDirectores]);

  // Handle submit - delete directors if switching to "abogado", then proceed
  const handleSubmit = async () => {
    setValidationError(null);
    setIsSubmitting(true);

    try {
      // Validate that gestion_directores is selected
      if (!gestionDirectores) {
        setValidationError(t("directors.gestion_directores.required"));
        return;
      }

      // If "abogado" is selected and director contacts exist, handle them on submit
      const isAbogadoSelected = gestionDirectores === GESTION_DIRECTORES[0];
      if (isAbogadoSelected && directorContacts.length > 0) {
        // Separate contacts: those who are ONLY directors vs those who are BOTH director AND shareholder
        const directorsOnly = directorContacts.filter(
          (contact) => !isShareholderContact(contact)
        );
        const directorsAndShareholders = directorContacts.filter(
          (contact) => isShareholderContact(contact)
        );

        // Delete contacts that are ONLY directors
        const deletePromises = directorsOnly
          .filter((contact) => contact.id)
          .map((contact) => deleteIncorporationContact(contact.id!));
        await Promise.all(deletePromises);

        // Update contacts that are BOTH director AND shareholder - clear cargo field
        const updatePromises = directorsAndShareholders
          .filter((contact) => contact.id)
          .map((contact) =>
            createOrUpdateIncorporationContact({
              ...contact,
              cargo: "", // Clear director role
              incorporation_request_id: data?.incorporationRequest?.id,
            })
          );
        await Promise.all(updatePromises);

        // Update local state
        setData((prevData) => {
          if (!prevData) return prevData;
          const updatedContacts = (prevData.incorporationContact || [])
            .filter((contact) => {
              // Keep contacts that are NOT directors
              if (!isDirectorContact(contact)) return true;
              // Keep contacts that are BOTH director AND shareholder (they were updated, not deleted)
              if (isShareholderContact(contact)) return true;
              // Remove contacts that are ONLY directors
              return false;
            })
            .map((contact) => {
              // Clear cargo for contacts that were both director and shareholder
              if (isDirectorContact(contact) && isShareholderContact(contact)) {
                return { ...contact, cargo: "" as typeof CARGO_DIRECTOR[number] };
              }
              return contact;
            });
          return {
            ...prevData,
            incorporationContact: updatedContacts,
            incorporationRequest: prevData.incorporationRequest || {},
            currentUser: prevData.currentUser,
            incorporationStripePrice: prevData.incorporationStripePrice,
            path: prevData.path,
          };
        });
      }

      // Proceed to next step
      next();
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Error al procesar la solicitud";
      setValidationError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout
      title={t("directors.title")}
      description={<Trans i18nKey="directors.description" />}
      maxWidth={950}
    >
      <span className="fw-bold size-18 mt-4">{t("directors.question")}</span>
      <div className="d-flex flex-column gap-4 text-start mt-4">
        {/* Gestion Directores Radio Group */}
        <div className="d-flex gap-4 flex-wrap">
          {gestionDirectoresOptions.map((option) => {
            const isChecked = gestionDirectores === option.value;
            const isOptionDisabled =
              isSavingGestionDirectores || option.disabled;

            return (
              <div
                key={option.value}
                className={`border rounded p-3 position-relative
                  ${isChecked ? "border-primary " : ""}  ${
                  isOptionDisabled ? "bg-light" : ""
                }`}
                style={{
                  cursor: isOptionDisabled ? "not-allowed" : "pointer",
                  backgroundColor: "#FFFFFF",
                  borderColor: isChecked ? undefined : "#dfdfdf",
                  borderRadius: "8px",
                  flex: 1,
                  opacity: isOptionDisabled ? 0.6 : 1,
                }}
                onClick={() => {
                  if (!isOptionDisabled) {
                    handleGestionDirectoresChange(option.value);
                  }
                }}
              >
                <span className="badge rounded-pill badge-blue position-absolute top-0 end-0 translate-middle">
                  {option.badge_text}
                </span>
                <div className="d-flex flex-column gap-1">
                  <div className="d-flex align-items-center gap-2">
                    <input
                      className="form-check-input"
                      type="radio"
                      id={option.value}
                      name="gestion_directores"
                      value={option.value}
                      checked={isChecked}
                      disabled={isOptionDisabled}
                      onChange={() => {
                        if (!isOptionDisabled) {
                          handleGestionDirectoresChange(option.value);
                        }
                      }}
                      style={{ marginTop: 0 }}
                    />
                    <label
                      className="form-check-label mb-0 fw-bold"
                      htmlFor={option.value}
                      style={{
                        cursor: isOptionDisabled ? "not-allowed" : "pointer",
                      }}
                    >
                      {option.label}
                      {isSavingGestionDirectores && isChecked && (
                        <span className="ms-2 text-muted">(Guardando...)</span>
                      )}
                    </label>
                    {option.tooltip && (
                      <CustomTooltip text={option.tooltip.text} />
                    )}
                  </div>
                  <span
                    className="text-muted mb-0 ms-4"
                    style={{ fontSize: "12px" }}
                  >
                    {option.subtitle}
                  </span>
                  {option.description && (
                    <div className="mb-0 ms-4 size-14">{option.description}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Error message for gestion_directores */}
        {gestionDirectoresError && (
          <div className="alert alert-danger" role="alert">
            {gestionDirectoresError}
          </div>
        )}

        {/* Validation error */}
        {validationError && (
          <div className="alert alert-danger" role="alert">
            {validationError}
          </div>
        )}

        {/* Continue Button */}
        <div className="d-flex justify-content-center w-100 w-md-50 mx-auto">
          <NextButton
            type="button"
            label={t("company_name.continue")}
            onClick={handleSubmit}
            disabled={isSubmitting || isSavingGestionDirectores}
          />
        </div>
      </div>
    </Layout>
  );
};

GestionDirectoresStep.title = (t) => t("gestion_directores_step.title");
GestionDirectoresStep.description = (t) =>
  t("gestion_directores_step.description_progress_panel");
GestionDirectoresStep.isComplete = (data: IncorporationWizardInitialData) => {
  const gestion = data.incorporationRequest?.gestion_directores;
  return !!gestion && GESTION_DIRECTORES.includes(gestion);
};

export default GestionDirectoresStep;
